import { BOUNCE_CONTROL_KEY_LABELS, BOUNCE_CONTROL_KEYS, type BounceControlKey } from "@station/contracts";
import { Alert, Button, Card, FormField, Input, Select, SkeletonRows, useToast } from "@station/ui";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { useInstrumentSettings } from "../../../features/instruments/queries.js";
import {
  InstrumentPage,
  useInstrumentPermissions,
  useRefreshInstruments,
} from "../../../features/instruments/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage } from "../../../lib/errors.js";

export const Route = createFileRoute("/_app/cek-senet/ayarlar")({
  validateSearch: (search) => z.object(companySearchShape).parse(search),
  component: InstrumentSettingsPage,
});

/** Gün sayısı alanları: 0–365 tam sayı. */
const DAY_FIELDS = [
  "collectionReminderDays",
  "ownPaymentReminderDays",
  "collateralReminderDays",
  "endorsementCloseDays",
  "riskyBounceThreshold",
] as const;
type DayField = (typeof DAY_FIELDS)[number];

function InstrumentSettingsPage() {
  const { t } = useTranslation();
  return (
    <InstrumentPage title={t("menu.instrumentSettings")} description={t("instruments.settings.description")}>
      {({ companyId }) => <SettingsForm companyId={companyId} />}
    </InstrumentPage>
  );
}

function SettingsForm({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshInstruments();
  const canManage = useInstrumentPermissions().canManageSettings;
  const settings = useInstrumentSettings(companyId);
  const [bounceKey, setBounceKey] = useState<BounceControlKey | null>(null);
  const [days, setDays] = useState<Partial<Record<DayField, string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (settings.isPending) return <SkeletonRows rows={5} />;
  if (settings.error) return <Alert tone="danger">{errorMessage(settings.error)}</Alert>;

  const current = settings.data;
  // Kullanıcı bir alana dokunmadıysa sunucudaki değer gösterilir (denetimli alanlar, geç gelen veriye dayanıklı).
  const value = (field: DayField): string => days[field] ?? String(current[field]);
  const numbers = Object.fromEntries(DAY_FIELDS.map((field) => [field, Number(value(field))])) as Record<
    DayField,
    number
  >;
  // Risk eşiği adet sayısıdır (1–100), diğerleri gün sayısıdır (0–365).
  const invalid = DAY_FIELDS.filter((field) => {
    const number = numbers[field];
    if (!Number.isInteger(number)) return true;
    return field === "riskyBounceThreshold" ? number < 1 || number > 100 : number < 0 || number > 365;
  });

  async function submit(): Promise<void> {
    if (invalid.length > 0) return setError(t("instruments.settings.invalidDays"));
    setBusy(true);
    setError(null);
    try {
      await api.PUT("/instrument-settings", {
        params: { query: { companyId } },
        body: { bounceReceivableKey: bounceKey ?? current.bounceReceivableKey, ...numbers },
      });
      await refresh(companyId);
      toast({ tone: "success", title: t("common.saved") });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="grid max-w-2xl gap-4 p-5">
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <FormField
          label={t("instruments.settings.bounceReceivableKey")}
          description={t("instruments.settings.bounceReceivableKeyHint")}
          required
        >
          {(control) => (
            <Select
              {...control}
              value={bounceKey ?? current.bounceReceivableKey}
              disabled={!canManage}
              onChange={(event) => setBounceKey(event.target.value as BounceControlKey)}
            >
              {BOUNCE_CONTROL_KEYS.map((key) => (
                <option key={key} value={key}>
                  {BOUNCE_CONTROL_KEY_LABELS[key]}
                </option>
              ))}
            </Select>
          )}
        </FormField>

        {DAY_FIELDS.map((field) => (
          <FormField
            key={field}
            label={t(`instruments.settings.fields.${field}`)}
            description={t(`instruments.settings.hints.${field}`)}
            error={
              invalid.includes(field)
                ? field === "riskyBounceThreshold"
                  ? t("instruments.settings.invalidThreshold")
                  : t("instruments.settings.invalidDays")
                : undefined
            }
            required
          >
            {(control) => (
              <Input
                {...control}
                value={value(field)}
                inputMode="numeric"
                className="tabular w-32"
                disabled={!canManage}
                onChange={(event) =>
                  setDays((previous) => ({ ...previous, [field]: event.target.value.replace(/\D/g, "") }))
                }
              />
            )}
          </FormField>
        ))}

        {canManage ? (
          <div className="flex justify-end">
            <Button type="submit" loading={busy}>
              {t("common.save")}
            </Button>
          </div>
        ) : (
          <Alert tone="info">{t("instruments.settings.readOnly")}</Alert>
        )}
      </form>
    </Card>
  );
}
